import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, BookOpen, CheckCircle2, ChevronLeft, X } from 'lucide-react';
import useScrollToTop from '../hooks/useScrollToTop';
import { CourseDetails, useCourseApi } from '../hooks/useCourseApi';
import { cardTheme } from '../styles/cardTheme';
import { mapCourseLoadError } from '../utils/courseLoadLabels';
import { getCurriculumUnitAccessStatus } from '../utils/curriculumUnitAccess';
import videoLessonIcon from '../assets/images/curriculum/video.png';
import quizLessonIcon from '../assets/images/curriculum/quiz.png';
import homeworkLessonIcon from '../assets/images/curriculum/homework.png';
import pdfLessonIcon from '../assets/images/curriculum/pdf.png';

const lessonTypeMeta = (type: string) => {
  if (type === 'Video') return { label: 'فيديو', icon: videoLessonIcon };
  if (type === 'Quiz') return { label: 'اختبار', icon: quizLessonIcon };
  if (type === 'Assignment') return { label: 'واجب', icon: homeworkLessonIcon };
  if (type === 'File') return { label: 'ملف', icon: pdfLessonIcon };
  return { label: type, icon: videoLessonIcon };
};

const isLessonCompleted = (lesson: CourseDetails['units'][number]['lessons'][number]) =>
  (lesson.type === 'Video' && lesson.isCompleted) ||
  (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
  (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
  (lesson.type === 'File' && lesson.isFileOpened);

const CourseUnitLessons: React.FC = () => {
  useScrollToTop();
  const { courseId, unitId } = useParams<{ courseId: string; unitId: string }>();
  const navigate = useNavigate();
  const { fetchCourseDetails, isLoading } = useCourseApi();
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!courseDetails || !courseId || !unitId) return;
    const index = courseDetails.units.findIndex((unit) => String(unit.id) === String(unitId));
    const unit = index >= 0 ? courseDetails.units[index] : null;
    if (!unit?.lessons?.length) return;
    const videoIndex = unit.lessons.findIndex((lesson) => lesson.type === 'Video');
    const lessonIndex = videoIndex >= 0 ? videoIndex : 0;
    const lesson = unit.lessons[lessonIndex];
    navigate(
      `/course-player/${courseId}?unit=${index}&lesson=${lessonIndex}&lessonId=${lesson.id}`,
      { replace: true }
    );
  }, [courseDetails, courseId, unitId, navigate]);

  useEffect(() => {
    if (!courseId) return;

    let cancelled = false;

    const loadCourseDetails = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        const response = await fetchCourseDetails(courseId);
        if (cancelled) return;

        if (response?.success) {
          setCourseDetails(response.data);
        } else {
          setError(
            response?.message ||
              (response === null ? 'حدث خطأ أثناء تحميل الدورة' : 'Course details not found.')
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading course:', err);
        setError('حدث خطأ أثناء تحميل الدورة');
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    loadCourseDetails();

    return () => {
      cancelled = true;
    };
  }, [courseId, fetchCourseDetails]);

  const backToCurriculum = () => navigate(`/course/${courseId}/curriculum`);

  if (initialLoading || (isLoading && !courseDetails)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className="w-full max-w-sm p-4 text-center sm:p-8">
          <div className="relative mx-auto mb-5 h-14 w-14 sm:mb-6 sm:h-16 sm:w-16">
            <div className="absolute inset-0 animate-spin rounded-full border-4 border-green-100 border-t-green-700" />
          </div>
          <h3 className={`mb-2 text-lg font-bold sm:text-xl ${cardTheme.title}`}>جاري تحميل الدروس</h3>
          <p className={`text-sm sm:text-base ${cardTheme.subtitle}`}>يرجى الانتظار بينما نحمّل دروس الوحدة...</p>
        </div>
      </div>
    );
  }

  if (error && !courseDetails) {
    const ui = mapCourseLoadError(!courseId ? 'missing course id' : error);
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}>
            <BookOpen className="h-7 w-7" />
          </div>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto sm:text-base ${cardTheme.btnPrimary}`}
          >
            {ui.buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  const unitIndex = courseDetails?.units.findIndex((unit) => String(unit.id) === String(unitId)) ?? -1;
  const unit = unitIndex >= 0 && courseDetails ? courseDetails.units[unitIndex] : null;

  if (!courseDetails || !unit) {
    const ui = mapCourseLoadError('Course details not found.');
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>الوحدة غير موجودة</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={backToCurriculum}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto sm:text-base ${cardTheme.btnPrimary}`}
          >
            ارجع للمنهج
          </button>
        </div>
      </div>
    );
  }

  const unitAccess = getCurriculumUnitAccessStatus(
    unitIndex,
    courseDetails.enrollmentDate,
    courseDetails.isOpenToAll
  );
  const isUnitLocked = !unitAccess.accessible;
  const completedInUnit = unit.lessons.filter(isLessonCompleted).length;

  const handleLessonClick = (lessonIndex: number) => {
    if (isUnitLocked) {
      setError(unitAccess.message || 'هذه الوحدة غير متاحة حالياً');
      return;
    }

    const lesson = unit.lessons[lessonIndex];
    if (!lesson) {
      setError('الدرس غير متوفر');
      return;
    }

    setError(null);
    navigate(
      `/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper font-cairo" dir="rtl">
      <header className="sticky top-0 z-20 border-b border-green-100/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-4xl items-center px-3 sm:px-6">
          <button
            type="button"
            onClick={backToCurriculum}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-green-800 hover:text-green-600"
          >
            <ArrowRight className="h-5 w-5" />
            المنهج
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-4 pb-24 sm:px-6 sm:py-8 sm:pb-10">
        <section className={`${cardTheme.shell} mb-5 overflow-hidden sm:mb-7`}>
          <div className={`${cardTheme.headerBanner} px-4 py-5 sm:px-6 sm:py-6`}>
            <div className={`mb-3 inline-flex ${cardTheme.badgeGold} border-gold-400/40 bg-gold-500/20 text-gold-200`}>
              <BookOpen className="h-3.5 w-3.5" />
              دروس الوحدة
            </div>
            <p className="mb-1 text-xs font-semibold text-green-100/80 sm:text-sm">{courseDetails.courseName}</p>
            <h1 className="mb-3 break-words text-xl font-bold leading-snug text-white sm:text-2xl">
              {unit.unitName}
            </h1>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white sm:text-xs">
                {unit.lessons.length} دروس
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-500/25 px-2.5 py-1 text-[11px] font-semibold text-gold-200 sm:text-xs">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {completedInUnit}/{unit.lessons.length} مكتمل
              </span>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 sm:mb-5">
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center"
              aria-label="إغلاق"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {isUnitLocked ? (
          <div className={`${cardTheme.shell} p-5 text-center sm:p-6`}>
            <p className={`text-sm font-bold sm:text-base ${cardTheme.title}`}>{unitAccess.statusText}</p>
            <p className={`mt-1 text-xs sm:text-sm ${cardTheme.subtitle}`}>
              {unitAccess.message || 'هذه الوحدة غير متاحة حالياً'}
            </p>
          </div>
        ) : unit.lessons.length === 0 ? (
          <div className={`${cardTheme.shell} px-4 py-8 text-center sm:px-6`}>
            <p className={`text-sm font-semibold ${cardTheme.title}`}>لسه مفيش دروس هنا</p>
            <p className={`mt-1 text-xs sm:text-sm ${cardTheme.subtitle}`}>
              الوحدة دي موجودة، بس المدرس لسه مضافش دروس جواها.
            </p>
          </div>
        ) : (
          <div className={`${cardTheme.shell} overflow-hidden`}>
            {unit.lessons.map((lesson, lessonIndex) => {
              const isCompleted = isLessonCompleted(lesson);
              const isQuizSubmitted = lesson.type === 'Quiz' && lesson.isQuizSubmitted === true;
              const isAssignmentSubmitted = lesson.type === 'Assignment' && lesson.isAssignmentSubmitted === true;
              const isFileOpened = lesson.type === 'File' && lesson.isFileOpened === true;
              const { label, icon } = lessonTypeMeta(lesson.type);

              return (
                <button
                  key={lesson.id}
                  type="button"
                  className="flex w-full min-h-14 items-start gap-3 border-b border-green-50 p-3.5 text-right last:border-b-0 hover:bg-green-50/40 sm:items-center sm:gap-4 sm:p-4"
                  onClick={() => handleLessonClick(lessonIndex)}
                >
                  <div
                    className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${
                      isCompleted ? 'bg-green-50 ring-2 ring-green-600/30' : 'border border-green-100 bg-white shadow-sm'
                    }`}
                  >
                    <img src={icon} alt={label} className="h-7 w-7 object-contain sm:h-8 sm:w-8" loading="lazy" />
                    {isCompleted && (
                      <span className="absolute -bottom-1 -left-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-700 text-white shadow">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h4 className="mb-1 break-words text-sm font-bold leading-snug text-green-900 sm:text-base">
                      {lesson.lessonName}
                    </h4>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-[11px] ${
                          lesson.type === 'Quiz'
                            ? 'border border-gold-200 bg-gold-50 text-gold-800'
                            : lesson.type === 'Assignment'
                              ? 'border border-amber-200 bg-amber-50 text-amber-900'
                              : 'border border-green-100 bg-green-50 text-green-800'
                        }`}
                      >
                        {label}
                      </span>
                      {isCompleted && (
                        <span className="text-[10px] font-semibold text-green-700 sm:text-[11px]">مكتمل</span>
                      )}
                      {(isQuizSubmitted || isAssignmentSubmitted) && !isCompleted && (
                        <span className="text-[10px] font-semibold text-green-700 sm:text-[11px]">تم الإرسال</span>
                      )}
                      {isFileOpened && !isCompleted && (
                        <span className="text-[10px] font-semibold text-gold-700 sm:text-[11px]">تم الفتح</span>
                      )}
                    </div>
                  </div>

                  <ChevronLeft className="mt-2 h-4 w-4 shrink-0 text-gray-400 sm:mt-0 sm:h-5 sm:w-5" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseUnitLessons;
